import React from 'react';
import {
  CHECKOUT_TEXT_MAX_LENGTH,
  PICKER_PHONE_MAX_LENGTH,
  isValidPhone,
} from '../utils/phone';

/**
 * The "who is picking up" block of the Manual Checkout (TXAM) form: name,
 * company and phone, all required.
 *
 * (2026-06-11, Jason Cox) Free-text picker identity -- a shared login means the
 * session can't identify the operator, so they type who's picking up and for
 * which company. Sent to the backend and stored on the ticket.
 * (2026-10-23, Jason Cox) Extracted from ManualCheckout.tsx when the phone field
 * was added (that page is well past the 500-line limit). The phone is stored as
 * a "Picker Phone:" line on the ticket note and shown in History and on the
 * billing report.
 */
export interface PickerDetailsFieldsProps {
  pickerName: string;
  pickerCompany: string;
  pickerPhone: string;
  onPickerNameChange: (value: string) => void;
  onPickerCompanyChange: (value: string) => void;
  onPickerPhoneChange: (value: string) => void;
  /** True after the first submit attempt; turns on the red "Required" styling. */
  showValidation: boolean;
}

/** Helper text under the phone field, or null when it's fine. */
function phoneError(phone: string, showValidation: boolean): string | null {
  if (!showValidation) return null;
  if (!phone.trim()) return 'Required';
  if (!isValidPhone(phone)) return 'Enter a full phone number, including area code';
  return null;
}

export default function PickerDetailsFields({
  pickerName,
  pickerCompany,
  pickerPhone,
  onPickerNameChange,
  onPickerCompanyChange,
  onPickerPhoneChange,
  showValidation,
}: PickerDetailsFieldsProps) {
  const phoneMessage = phoneError(pickerPhone, showValidation);

  // (2026-10-06) autoComplete="off" on every field: the browser was filling in
  // values from earlier checkouts, which risks the wrong person on a ticket.
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="block text-xs font-medium text-secondary-700">
          Who is picking up<span className="text-red-500 ml-0.5">*</span>
        </label>
        <input
          value={pickerName}
          autoComplete="off"
          maxLength={CHECKOUT_TEXT_MAX_LENGTH}
          onChange={(e) => onPickerNameChange(e.target.value)}
          className={`${showValidation && !pickerName.trim() ? 'input-error' : 'input'} mt-1 w-full`}
          placeholder="Name of person picking up"
        />
        {showValidation && !pickerName.trim() && (
          <p className="mt-1 text-xs text-error-600">Required</p>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-secondary-700">
          Picking-up company<span className="text-red-500 ml-0.5">*</span>
        </label>
        <input
          value={pickerCompany}
          autoComplete="off"
          maxLength={CHECKOUT_TEXT_MAX_LENGTH}
          onChange={(e) => onPickerCompanyChange(e.target.value)}
          className={`${showValidation && !pickerCompany.trim() ? 'input-error' : 'input'} mt-1 w-full`}
          placeholder="Company the person is picking up for"
        />
        {showValidation && !pickerCompany.trim() && (
          <p className="mt-1 text-xs text-error-600">Required</p>
        )}
      </div>
      <div>
        <label htmlFor="picker-phone" className="block text-xs font-medium text-secondary-700">
          Picker phone<span className="text-red-500 ml-0.5">*</span>
        </label>
        {/* type="tel" + inputMode="tel" brings up the phone keypad on the
            handheld; formatting is free-form and checked by digit count. */}
        <input
          id="picker-phone"
          type="tel"
          inputMode="tel"
          value={pickerPhone}
          autoComplete="off"
          maxLength={PICKER_PHONE_MAX_LENGTH}
          onChange={(e) => onPickerPhoneChange(e.target.value)}
          className={`${phoneMessage ? 'input-error' : 'input'} mt-1 w-full`}
          placeholder="Phone of person picking up"
        />
        {phoneMessage && <p className="mt-1 text-xs text-error-600">{phoneMessage}</p>}
      </div>
    </div>
  );
}
